import { Calendar, Image as ImageIcon } from 'lucide-react';
import { useState } from 'react';
import eventsBackground from '../assets/events-background.jpg';
// Import gallery images
import event1 from '../assets/gallery/event1.jpg';
import event2 from '../assets/gallery/event2.jpg';

export default function Events() {
  const [selectedImage, setSelectedImage] = useState<string | null>(null);
  const upcomingEvents = [
    { title: 'Nuclear Informational Tabling', date: 'TBA', time: 'TBA', location: 'Pierpont Commons' },
  ];

  const pastEvents = [
    { title: 'U of M Winterfest', date: 'January 13, 2026', time: '4:00 PM', location: 'Michigan Union' },
    { title: 'National Student March for Nuclear', date: 'October 8, 2025', time: '3:00 PM', location: 'Mortimer E. Cooley' },
    { title: 'Festifall', date: 'August 27, 2025', time: '4:30 PM', location: 'Ingalls Mall' },
    { title: 'Lansing Hill Day', date: 'February 18', time: '9:00 AM', location: 'Lansing' },
  ];

  // Photo gallery - you'll need to add these images to src/assets/gallery/
  const galleryPhotos = [
    { src: event1, alt: 'National Student March for Nuclear', caption: 'MANE Students showing their support for nuclear energy' },
    { src: event2, alt: 'Lansing Hill Day', caption: 'MANE students in front of the Michigan Capitol building' },
  ];

  return (
    <section className="pt-24 py-16 px-4 sm:px-6 lg:px-8 bg-gray-50 min-h-screen relative overflow-hidden">
      <div 
        className="absolute inset-0 bg-cover bg-center bg-no-repeat opacity-55"
        style={{ backgroundImage: `url(${eventsBackground})` }}
      />
      <div className="absolute inset-0 bg-gray-50 opacity-70" />
      
      <div className="max-w-7xl mx-auto relative z-10">
        <div 
          className="flex items-center justify-center mb-12"
          data-aos="fade-down"
        >
          <Calendar className="w-8 h-8 text-michigan-blue mr-3" />
          <h2 className="text-4xl font-bold text-michigan-blue">Events</h2>
        </div>
        
        {/* Upcoming Events */}
        <div className="mb-12">
          <h3 
            className="text-3xl font-bold text-michigan-blue mb-6"
            data-aos="fade-right"
          >
            Upcoming Events
          </h3>
          <div className="grid md:grid-cols-3 gap-6">
            {upcomingEvents.map((event, idx) => (
              <div 
                key={idx} 
                className="bg-white rounded-xl shadow-lg p-6 hover:shadow-xl transition border-t-4 border-michigan-maize"
                data-aos="fade-up"
                data-aos-delay={idx * 100}
              >
                <h4 className="text-xl font-semibold text-michigan-blue mb-3">{event.title}</h4>
                <div className="space-y-2 text-gray-600">
                  <p className="flex items-start">
                    <span className="font-medium mr-2">Date:</span>
                    <span>{event.date}</span>
                  </p>
                  <p className="flex items-start">
                    <span className="font-medium mr-2">Time:</span>
                    <span>{event.time}</span>
                  </p>
                  <p className="flex items-start">
                    <span className="font-medium mr-2">Location:</span>
                    <span>{event.location}</span>
                  </p>
                </div>
              </div>
            ))}
          </div>
        </div>

        {/* Past Events */}
        <div className="mb-16">
          <h3 
            className="text-3xl font-bold text-michigan-blue mb-6"
            data-aos="fade-right"
          >
            Past Events
          </h3>
          <div className="grid md:grid-cols-3 gap-6">
            {pastEvents.map((event, idx) => (
              <div 
                key={idx} 
                className="bg-gray-100 rounded-xl shadow p-6 border-t-4 border-gray-400 opacity-90"
                data-aos="fade-up"
                data-aos-delay={idx * 100}
              >
                <h4 className="text-xl font-semibold text-gray-700 mb-3">{event.title}</h4>
                <div className="space-y-2 text-gray-600">
                  <p className="flex items-start">
                    <span className="font-medium mr-2">Date:</span>
                    <span>{event.date}</span>
                  </p>
                  <p className="flex items-start">
                    <span className="font-medium mr-2">Time:</span>
                    <span>{event.time}</span>
                  </p>
                  <p className="flex items-start">
                    <span className="font-medium mr-2">Location:</span>
                    <span>{event.location}</span>
                  </p>
                </div>
              </div>
            ))}
          </div>
        </div>

        {/* Photo Gallery */}
        <div data-aos="fade-up">
          <div className="flex items-center justify-center mb-8">
            <ImageIcon className="w-8 h-8 text-michigan-blue mr-3" />
            <h3 className="text-3xl font-bold text-michigan-blue">Photo Gallery</h3>
          </div>
          <p className="text-center text-gray-600 mb-8 max-w-2xl mx-auto">
            Take a look at our past events and activities. Click on any photo to view it in full size.
          </p>
          <div className="grid md:grid-cols-3 gap-6">
            {galleryPhotos.map((photo, idx) => (
              <div 
                key={idx}
                className="bg-white rounded-xl shadow-lg overflow-hidden hover:shadow-xl transition cursor-pointer group"
                data-aos="zoom-in"
                data-aos-delay={idx * 100}
                onClick={() => setSelectedImage(photo.src)}
              >
                <div className="relative overflow-hidden h-64">
                  <img 
                    src={photo.src} 
                    alt={photo.alt}
                    className="w-full h-full object-cover group-hover:scale-110 transition duration-300"
                  />
                  <div className="absolute inset-0 bg-michigan-blue opacity-0 group-hover:opacity-20 transition" />
                </div>
                <div className="p-4">
                  <p className="text-gray-700 font-medium text-center">{photo.caption}</p>
                </div>
              </div>
            ))}
          </div>
        </div>
      </div>

      {/* Lightbox Modal */}
      {selectedImage && (
        <div 
          className="fixed inset-0 bg-black bg-opacity-90 z-50 flex items-center justify-center p-4"
          onClick={() => setSelectedImage(null)}
        >
          <div className="relative max-w-5xl max-h-screen">
            <button 
              onClick={() => setSelectedImage(null)}
              className="absolute top-4 right-4 text-white text-4xl hover:text-michigan-maize transition"
            >
              ×
            </button>
            <img 
              src={selectedImage} 
              alt="Full size"
              className="max-w-full max-h-screen object-contain"
            />
          </div>
        </div>
      )}
    </section>
  );
}